/**
 * `<DetailedTestResultsView>` — the shared component that renders
 * the detailed-test-results view model.
 *
 * Lives under `components/` (not `print/layouts/`) so it can be
 * reused by both:
 *   - the PDF print layout (today, via the thin wrapper at
 *     `print/layouts/DetailedTestResultsLayout.tsx`), AND
 *   - a future native screen page that replaces the iframe at
 *     `pages/RunReport.tsx` (when we decide to kill the iframe;
 *     see the design conversation that produced this PR).
 *
 * Mode-aware via `usePrintMode()`: print mode expands everything,
 * disables interactivity, applies page-break-friendly CSS; screen
 * mode (not exercised in v1, but the shape is in place) would add
 * collapse toggles, sticky file headers, hover states. The "stub"
 * branches for screen mode are intentional — they keep the
 * component shape correct for future native rendering without
 * shipping unexercised code today.
 *
 * Presentation only. No data fetches, no Mongo, no API calls. The
 * view model is fully self-contained per the snapshot pattern.
 */
import { fmtDateTime, fmtDuration } from '@/print/format'
import { usePrintMode } from '@/print/RenderModeContext'
import type {
  DetailedTestResultsViewModel,
  DtrFileSection,
  DtrStep,
  DtrTest,
  IssueConsoleEvent,
  IssueNetworkEvent,
} from '@/print/types'

export interface DetailedTestResultsViewProps {
  viewModel: DetailedTestResultsViewModel
}

export function DetailedTestResultsView({
  viewModel,
}: DetailedTestResultsViewProps) {
  const { context, summary, files, definition } = viewModel
  // Defense-in-depth: `context.tags` is typed as `Record<string, string>`
  // and the orchestrator always builds it via `tagsFromRun` (returns
  // `{}` on missing input). In practice `Object.entries(context.tags)`
  // STILL threw "Cannot convert undefined or null to object" in
  // production — Mongoose's Mixed-type snapshot round-trip drops
  // empty objects somewhere between write and read. Guard so a
  // missing tags field can't crash the entire layout (which leaves a
  // blank PDF — the failure mode that prompted #824).
  const tagEntries = Object.entries(context.tags ?? {})

  // Print-mode chrome: max-width sized for Letter (8.5in - 0.4in
  // margins), white background, gray-on-white typography. Screen
  // mode (future) would expand to full viewport width.
  const printMode = usePrintMode()
  const rootClass = printMode
    ? 'bg-white text-gray-900 mx-auto max-w-[8.5in] p-8 font-sans text-sm leading-snug'
    : 'bg-white text-gray-900 w-full p-6 font-sans text-sm leading-snug'

  return (
    <div className={rootClass}>
      {/* Header — title is the run-type-aware framing per the design
          conversation; v1 endpoint serves replay only, so the title
          is always "Regression Report". A future discovery PDF
          would branch here. The "OttoTester ·" prefix was dropped
          in #841 — brand identity is already established by the
          per-page PDF header (logo + wordmark) AND the footer
          ("Generated by OttoTester"); a third instance on the
          body eyebrow was redundant. */}
      <header className="border-b border-gray-300 pb-4">
        <div className="text-[10px] tracking-widest text-gray-500">
          Regression Report
        </div>
        <h1 className="mt-1 text-2xl font-bold">
          {context.suiteName ?? context.variantName}
        </h1>
        <div className="mt-0.5 text-sm text-gray-600">
          {context.appName}
          {context.suiteName ? ` · ${context.variantName}` : ''}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1">
            <span className="opacity-70">Run</span>
            <code className="font-mono text-gray-700">{context.runId}</code>
          </span>
          <span>·</span>
          <span>Started {fmtDateTime(context.startedAt)}</span>
          <span>·</span>
          <span>Duration {fmtDuration(context.durationMs)}</span>
        </div>
        {tagEntries.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span className="text-gray-500">Tags:</span>
            {tagEntries.map(([k, v]) => (
              <span
                key={k}
                className="rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 font-mono text-[11px] text-gray-700"
              >
                {k}={v}
              </span>
            ))}
          </div>
        )}
      </header>

      {/* Count strip — quick visual orientation at the top of the
          document. Mirrors the dashboard's count chip set. */}
      <section className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <CountChip label="Total" value={summary.total} tone="muted" />
        <CountChip label="Passed" value={summary.passed} tone="success" />
        <CountChip label="Failed" value={summary.failed} tone="destructive" />
        <CountChip label="Skipped" value={summary.skipped} tone="muted" />
      </section>

      {/* Body — file sections. Empty state is the rare zero-test
          run (very-early crash that still produced a summary doc);
          we render a friendly empty card rather than nothing so
          the reader knows the PDF "worked" but there's nothing to
          show. */}
      {files.length === 0 ? (
        <section className="mt-6 rounded border border-dashed border-gray-300 p-4 text-center text-sm text-gray-600">
          No test executions recorded for this run.
        </section>
      ) : (
        <section className="mt-6 space-y-4">
          {files.map((file, i) => (
            <FileSection
              key={`${file.filePath ?? '__unattributed__'}-${i}`}
              file={file}
            />
          ))}
        </section>
      )}

      {/* Footer */}
      <footer className="mt-8 border-t border-gray-300 pt-3 text-[10px] text-gray-500">
        <div>
          Generated by OttoTester · {definition.id} v{definition.version} ·{' '}
          {fmtDateTime(viewModel.generatedAt)}
        </div>
        <div className="mt-1">
          Live view:{' '}
          <a className="underline" href={viewModel.liveViewUrl}>
            {viewModel.liveViewUrl}
          </a>
        </div>
      </footer>
    </div>
  )
}

// ── File section ────────────────────────────────────────────────────

/**
 * One spec file's worth of tests. Renders a small header
 * (`checkout.spec.ts · 24 tests · 2 failing`) and the file's
 * tests in the order the orchestrator gave them (failing →
 * skipped → passing per the testReporting query's sort).
 *
 * Print-mode CSS (updated in #828 after the original avoid-per-card
 * rule produced page-bottom whitespace):
 *   - `pageBreakAfter: 'avoid'` on the file header so it doesn't
 *     end up orphaned at the bottom of a page with all its tests
 *     pushed to the next.
 *   - No `pageBreakInside` on the article or the test list — both
 *     flow naturally across pages. A long error message that
 *     straddles a page boundary continues on the next page, which
 *     reads much better than the alternative (huge whitespace
 *     under a file header).
 *   - `pageBreakInside: 'avoid'` is scoped narrowly to the
 *     screenshot wrapper inside TestCard so images don't split
 *     mid-pixel; everything else flows freely.
 */
function FileSection({ file }: { file: DtrFileSection }) {
  const failingCount = file.counts.failed
  // Show just the spec file's basename — the full repo-relative path
  // is noise in a PDF (every test from `tests/checkout.spec.ts` is
  // visibly under that section already; the directory prefix
  // contributes nothing). `filePath` is `null` for the rare legacy
  // row that has no testFile attribution — those bucket under
  // "Unattributed" instead.
  const displayName = file.filePath
    ? file.filePath.split('/').pop() || file.filePath
    : 'Unattributed'
  return (
    <article className="rounded border border-gray-200">
      {/* `pageBreakAfter: 'avoid'` keeps the file header glued to at
          least one test row — without it, a file header at the bottom
          of a page can end up alone (orphan) with all its tests on
          the next page. */}
      <header
        className="bg-gray-50 px-3 py-2 border-b border-gray-200"
        style={{ pageBreakAfter: 'avoid' }}
      >
        <div
          className="font-mono text-xs text-gray-800"
          title={file.filePath ?? undefined}
        >
          {displayName}
        </div>
        <div className="mt-0.5 text-[11px] text-gray-500">
          {file.counts.total} {file.counts.total === 1 ? 'test' : 'tests'}
          {failingCount > 0 ? ` · ${failingCount} failing` : ''}
          {file.counts.skipped > 0 ? ` · ${file.counts.skipped} skipped` : ''}
        </div>
      </header>
      {/* No `pageBreakInside: 'avoid'` on the test list items — earlier
          versions forced each test card onto a single page, which
          left huge whitespace below a file header whenever a tall
          failing test (error + screenshot) couldn't fit on the
          current page. Cards now flow naturally; the screenshot
          element inside TestCard carries its own avoid-split rule
          so an image still won't get cut mid-pixel. */}
      <ul className="divide-y divide-gray-100">
        {file.tests.map((test) => (
          <li key={test.testId}>
            <TestCard test={test} />
          </li>
        ))}
      </ul>
    </article>
  )
}

// ── Test card ───────────────────────────────────────────────────────

/**
 * One test's row. Unified card layout across all statuses
 * (#841 dropped the prior compact-one-liner branch for passing
 * + skipped — user feedback was that a "detailed test report"
 * implies step-level detail on EVERY test, not just failures).
 *
 * The card conditionally renders error / screenshot blocks only
 * when those fields are present, so passing/skipped tests still
 * read leaner than failing ones (no error pre, no image) — just
 * with their step list visible underneath.
 */
function TestCard({ test }: { test: DtrTest }) {
  return (
    <div className="px-3 py-2.5 text-[12px]">
      <div className="flex items-baseline gap-2">
        <StatusDot status={test.status} />
        <div className="flex-1 font-medium text-gray-900">{test.testName}</div>
        <div className="text-[11px] text-gray-500 tabular-nums">
          {fmtDuration(test.durationMs)}
        </div>
      </div>
      {/* The metadata strip only renders content when there's
          something to show (errorType or retries). Passing tests
          typically have neither — the strip collapses to nothing
          visible, no orphan whitespace. */}
      {(test.errorType || test.retryCount > 0) && (
        <div className="mt-1 ml-4 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500">
          {test.errorType && (
            <span className="font-mono text-gray-700">{test.errorType}</span>
          )}
          {test.retryCount > 0 && (
            <>
              {test.errorType && <span>·</span>}
              {/* `retryCount > 0` means at least one retry happened,
                  so the total attempt count (`retryCount + 1`) is
                  always 2+ — always plural. */}
              <span>{test.retryCount + 1} attempts</span>
            </>
          )}
        </div>
      )}
      {test.steps && test.steps.length > 0 && (
        <StepList steps={test.steps} />
      )}
      {/* Captured console/network events (#1515 Slice 4) — after the
          step list, before the error block, matching the in-app card. */}
      {((test.console?.length ?? 0) > 0 ||
        (test.network?.length ?? 0) > 0) && (
        <IssueEventsBlock test={test} />
      )}
      {test.errorMessage && (
        <pre className="mt-1.5 ml-4 max-w-full whitespace-pre-wrap break-words rounded bg-gray-50 px-2 py-1 font-mono text-[11px] leading-snug text-gray-800">
          {test.errorMessage}
        </pre>
      )}
      {test.screenshot && (
        // `pageBreakInside: 'avoid'` on the screenshot wrapper —
        // splitting an image mid-pixel produces a broken artifact
        // (visible top half on one page, nothing on the next).
        // Scoped to just the image; the surrounding test card flows
        // freely so the error message can break across pages if
        // needed. The image is small (2.5"×1.5" max) so it always
        // fits on a page; the avoid rule just decides which page.
        <div className="mt-2 ml-4" style={{ pageBreakInside: 'avoid' }}>
          {/* Screenshot ref is mode-agnostic — data URL (PDF) or
              authed URL (future native). The same <img> tag
              handles both because both are valid `src` values. */}
          <img
            src={test.screenshot}
            alt={`Failure screenshot for ${test.testName}`}
            className="max-w-[2.5in] rounded border border-gray-200"
            style={{ maxHeight: '1.5in', objectFit: 'contain' }}
          />
        </div>
      )}
      <div className="mt-2 ml-4 text-[11px]">
        <a
          className="text-gray-600 underline hover:text-gray-900"
          href={test.liveReportUrl}
        >
          View in detailed report →
        </a>
      </div>
    </div>
  )
}

/** `1234` → `1.2s` — seconds from test start (#1515 Slice 4). */
function fmtRelMs(ms: number): string {
  return `${(ms / 1000).toFixed(1)}s`
}

/** One interleaved timeline of a test's captured console + network
 * events (#1515 Slice 4) — the same ordering as the in-app Issues card,
 * print-styled. Console = amber, network = violet. */
function IssueEventsBlock({ test }: { test: DtrTest }) {
  type TLEvent =
    | { kind: 'console'; relativeMs: number; c: IssueConsoleEvent }
    | { kind: 'network'; relativeMs: number; n: IssueNetworkEvent }
  const events: TLEvent[] = [
    ...(test.console ?? []).map(
      (c): TLEvent => ({ kind: 'console', relativeMs: c.relativeMs, c }),
    ),
    ...(test.network ?? []).map(
      (n): TLEvent => ({ kind: 'network', relativeMs: n.relativeMs, n }),
    ),
  ].sort((a, b) => a.relativeMs - b.relativeMs)

  const consoleCount = test.console?.length ?? 0
  const networkCount = test.network?.length ?? 0
  const heading = [
    consoleCount > 0 ? `${consoleCount} console` : null,
    networkCount > 0 ? `${networkCount} network` : null,
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="mt-1.5 ml-4 rounded border border-gray-200 bg-gray-50 px-2.5 py-2">
      <div className="mb-1.5 text-[9.5px] font-semibold uppercase tracking-wide text-gray-500">
        Captured issues — {heading}
      </div>
      <div>
        {events.map((ev, i) => (
          <div
            key={i}
            className="grid grid-cols-[38px_14px_1fr] items-start gap-2 border-t border-dashed border-gray-200 pt-1 first:border-t-0 first:pt-0"
          >
            <span className="pt-px text-right font-mono text-[10px] text-gray-400">
              {fmtRelMs(ev.relativeMs)}
            </span>
            <span
              className={
                'mt-px flex h-3 w-3 items-center justify-center rounded-[2px] text-[8px] font-bold text-white ' +
                (ev.kind === 'console' ? 'bg-amber-600' : 'bg-violet-600')
              }
            >
              {ev.kind === 'console' ? '!' : '↔'}
            </span>
            <div className="min-w-0">
              {ev.kind === 'console' ? (
                <>
                  <div className="font-mono text-[10px]">
                    <span className="font-semibold text-gray-600">
                      {ev.c.kind}
                    </span>
                    {ev.c.errorName && (
                      <span className="ml-1.5 font-semibold text-red-700">
                        {ev.c.errorName}
                      </span>
                    )}
                  </div>
                  <div className="break-words font-mono text-[11px] text-gray-800">
                    {ev.c.message}
                  </div>
                </>
              ) : (
                <>
                  <div className="font-mono text-[10px]">
                    <span className="font-semibold text-gray-600">
                      {ev.n.method}
                    </span>
                    <NetEventStatus n={ev.n} />
                  </div>
                  <div className="break-words font-mono text-[11px] text-gray-500">
                    {ev.n.url}
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
      {test.issuesOmitted ? (
        <div className="mt-1 text-[10px] text-gray-500">
          +{test.issuesOmitted} more in the app.
        </div>
      ) : null}
    </div>
  )
}

/** HTTP status chip on a network event — 5xx red, 4xx amber, pending
 * neutral (#1515 Slice 4). */
function NetEventStatus({ n }: { n: IssueNetworkEvent }) {
  if (n.pending) {
    return (
      <span className="ml-1.5 rounded bg-gray-200 px-1 font-mono text-[9.5px] font-bold text-gray-600">
        pending
      </span>
    )
  }
  if (n.status === undefined) return null
  const tone =
    n.status >= 500
      ? 'bg-red-100 text-red-700'
      : 'bg-amber-100 text-amber-800'
  return (
    <span className={'ml-1.5 rounded px-1 font-mono text-[9.5px] font-bold ' + tone}>
      {n.status}
    </span>
  )
}

// ── Small UI primitives ─────────────────────────────────────────────

function StatusDot({
  status,
}: {
  status: DtrTest['status']
}) {
  const tone =
    status === 'passed'
      ? 'bg-emerald-500'
      : status === 'skipped'
        ? 'bg-gray-400'
        : 'bg-red-500'
  return (
    <span
      aria-label={`status: ${status}`}
      className={`inline-block h-2 w-2 rounded-full ${tone}`}
    />
  )
}

function CountChip({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: 'success' | 'destructive' | 'muted'
}) {
  const valueClass =
    tone === 'success'
      ? 'text-emerald-700'
      : tone === 'destructive'
        ? 'text-red-700'
        : 'text-gray-700'
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className={`tabular-nums font-semibold ${valueClass}`}>{value}</span>
      <span className="text-gray-600">{label.toLowerCase()}</span>
    </span>
  )
}

// ── Step list (Rich tier) ───────────────────────────────────────────

/**
 * Per-step breakdown rendered inside ANY test card (#841 dropped
 * the failing-only restriction after user feedback that a
 * "detailed test report" should show step-level detail on every
 * test). Each step carries its own status icon + name + duration;
 * failed steps also surface their truncated error message inline.
 *
 * Nested steps (`parentStepIndex` points at the enclosing row in
 * the same array) get indented one level — visual approximation
 * of Playwright's `test.step()` tree without an actual tree
 * traversal. Multi-level nesting collapses to one indent level;
 * that's a deliberate trade for layout simplicity.
 *
 * Step count is unbounded — a test with 30 steps renders 30 rows.
 * The screenshot below uses `pageBreakInside: avoid` so it stays
 * whole; the step list is allowed to flow across pages naturally
 * (a half-list on one page reading top-to-bottom is fine).
 */
function StepList({ steps }: { steps: DtrStep[] }) {
  return (
    <div className="mt-1.5 ml-4">
      <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-gray-500">
        Steps ({steps.length})
      </div>
      <ul className="space-y-0.5">
        {steps.map((s, i) => (
          <li
            key={`${s.name}-${i}`}
            className="text-[11px] leading-snug"
            style={{
              paddingLeft:
                typeof s.parentStepIndex === 'number' ? '1rem' : '0',
            }}
          >
            <div className="flex items-baseline gap-1.5">
              <StepStatusIcon status={s.status} />
              <span className="flex-1 font-mono text-gray-800">{s.name}</span>
              <span className="text-gray-500 tabular-nums">
                {/* `0ms` is a legitimate value (instant steps); only
                    fall back to `—` when the value is actually
                    missing / non-numeric. Negative is treated as
                    missing too — that's bad data, not a real reading. */}
                {typeof s.durationMs === 'number' && s.durationMs >= 0
                  ? `${s.durationMs}ms`
                  : '—'}
              </span>
            </div>
            {s.status === 'failed' && s.errorMessage && (
              <pre className="mt-0.5 ml-5 max-w-full whitespace-pre-wrap break-words font-mono text-[10px] leading-snug text-red-800">
                {s.errorMessage}
              </pre>
            )}
          </li>
        ))}
      </ul>
    </div>
  )
}

function StepStatusIcon({ status }: { status: DtrStep['status'] }) {
  // Tiny status glyphs sized to match the step text baseline.
  // Unicode glyphs (✓ ✗ ·) render predictably in headless Chromium
  // without needing icon-font support — keeps the print bundle
  // dependency-free.
  if (status === 'passed') {
    return (
      <span aria-label="passed" className="inline-block w-3 text-emerald-600">
        ✓
      </span>
    )
  }
  if (status === 'failed') {
    return (
      <span aria-label="failed" className="inline-block w-3 text-red-600">
        ✗
      </span>
    )
  }
  return (
    <span aria-label="skipped" className="inline-block w-3 text-gray-400">
      ·
    </span>
  )
}
